/* eslint no-console:0 */
"use strict";

import {
  query,
  queryOne,
  queryChildren,
  queryChild
} from "@standard-library/q-prime";
import { addIndex, all, equals, forEach, map, without } from "ramda";
import { Kefir as K } from "kefir";
import domready from "domready";
import galvo from "@standard-library/galvo";
import MoveTo from "moveto";

import elementBounds from "./modules/elementBounds";
import GoogleMaps from "./modules/topo/google_maps";
import slideshow from "./modules/slideshow";
import topo from "./modules/topo/topo";

const toggleClass = (className, element, predicate) => {
  if (predicate) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
};

domready(() => {
  const clientLists = query(".clients__list");
  const header = queryOne(".header");
  const hero = queryOne(".hero");
  const logo = queryOne(".logo");
  const maps = query(".map");
  const navigation = queryOne(".navigation");
  const navigationToggle = queryOne(".header__navigation-toggle");
  const observations = queryOne(".observations");
  const textLinks = query(".post__body a, .project__description a, .project__brief a");
  const slideshows = query(".slideshow");
  const staffMembers = query(".staff-member-item");
  const subnavigations = query(".subnavigation");

  const resize = K.fromEvents(window, "resize");
  const scroll = K.fromEvents(window, "scroll");
  const keyDown = K.fromEvents(window, "keydown");
  const escapeDown = keyDown.filter(e => e.which === 27);

  const windowHeight = resize
    .map(() => window.innerHeight)
    .toProperty(() => window.innerHeight);
  const bodyHeight = resize
    .map(() => document.body.offsetHeight)
    .toProperty(() => document.body.offsetHeight);
  const scrollY = scroll
    .map(() => window.scrollY)
    .toProperty(() => window.scrollY);

  const navigationToggleClick = K.fromEvents(navigationToggle, "click");
  const mobileNavigationVisible = navigationToggleClick.scan(p => !p, false);

  mobileNavigationVisible.observe(visible => {
    toggleClass("is-above-navigation", logo, visible);
    toggleClass("is-active", navigation, visible);
    toggleClass("is-toggled", navigationToggle, visible);
  });

  forEach(container => {
    const slides = queryChildren(container, ".slideshow__slide");

    if (slides.length > 0) {
      slideshow({ container, slides });
    }
  }, slideshows);

  if (hero) {
    const container = queryChild(hero, ".hero__images");
    const slides = queryChildren(hero, ".hero__image");
    const text = queryChild(hero, ".hero__text");
    const downArrow = queryChild(hero, ".hero__skip");

    const skipHero = K.fromEvents(downArrow, "click");

    const heroHeight = resize
      .map(() => hero.offsetHeight)
      .toProperty(() => hero.offsetHeight);

    const skipDestination = heroHeight.sampledBy(skipHero);

    const atEnd = K.combine(
      [scrollY, windowHeight, bodyHeight],
      (y, w, b) => y >= b - w
    ).skipDuplicates();

    const aboveFold = K.combine([scrollY, heroHeight], (y, h) => y < h)
      .toProperty()
      .skipDuplicates();

    const belowFold = aboveFold.map(a => !a);

    const mechanism = slideshow({
      container,
      slides,
      start: K.constant(Math.floor(Math.random() * slides.length)),
      advance: K.interval(6000).filterBy(aboveFold)
    });

    const textStyle = mechanism.current.map(c =>
      c.getAttribute("data-text-color")
    );

    const navigationStyle = K.merge([
      belowFold.filter(a => a).map(() => "dark"),
      textStyle.sampledBy(aboveFold.filter(a => a)),
      textStyle.filterBy(aboveFold)
    ]);

    const heroBounds = elementBounds(resize, hero);
    const textBounds = elementBounds(K.merge([scroll.take(1), resize]), text);
    const textVisible = K.combine(
      [scrollY, heroBounds.height, textBounds.height],
      (y, h, t) => y < h / 2 - t / 2
    ).toProperty();

    const arrowHidden = scrollY.map(y => y > 15);

    textStyle.observe(style => {
      text.classList.remove("hero__text--light");
      text.classList.remove("hero__text--dark");
      text.classList.add(`hero__text--${style}`);
    });

    navigationStyle.observe(style => {
      if (style === "light") {
        logo.classList.add("logo--inverted");
        navigation.classList.add("navigation--inverted");
        navigationToggle.classList.add("is-inverted");
        downArrow.classList.add("is-inverted");
      } else {
        logo.classList.remove("logo--inverted");
        navigation.classList.remove("navigation--inverted");
        navigationToggle.classList.remove("is-inverted");
        downArrow.classList.remove("is-inverted");
      }
    });

    textVisible.observe(visible => {
      toggleClass("is-hidden", text, !visible);
    });

    aboveFold.observe(above => {
      toggleClass("is-transparent", header, above);
      subnavigations.forEach(s => toggleClass("is-transparent", s, above));
    });

    arrowHidden.observe(hidden => {
      toggleClass("is-disabled", downArrow, hidden);
    });

    skipDestination.observe(y => {
      new MoveTo().move(y);
    });

    atEnd.observe(e => {
      toggleClass("is-hidden", hero, e);
    });
  }

  if (staffMembers.length) {
    const staffClicks = K.merge(
      map(s => {
        return K.fromEvents(s, "click").map(() => s);
      }, staffMembers)
    );

    staffClicks.observe(active => {
      const classList = active.classList;

      forEach(
        s => s.classList.remove("is-active"),
        without([active], staffMembers)
      );

      active.classList.toggle("is-active");
    });
  }

  if (clientLists.length) {
    const swap = K.interval(4000);

    addIndex(forEach)((list, i) => {
      const items = queryChildren(list, ".client-item");
      const index = K.constant(Math.floor(Math.random() * items.length));

      const display = galvo(
        {
          advance: swap,
          index
        },
        items
      );

      display.current.observe(function(current) {
        forEach(i => i.classList.remove("is-active"), items);
        current.classList.add("is-active");
      });
    }, clientLists);
  }

  forEach(mapElement => {
    const latitude = mapElement.getAttribute("data-latitude");
    const longitude = mapElement.getAttribute("data-longitude");
    const style = mapElement.getAttribute("data-google-style");
    const homeSource = mapElement.getAttribute("data-home-source");
    const center = [latitude, longitude];

    const places = K.constant([]);

    const atlas = GoogleMaps({
      style,
      zoom: 17
    });

    const ui = topo({
      element: mapElement,
      atlas: atlas,
      places: places,
      center: center
    });

    ui.addMarker({
      id: "home",
      slug: "home",
      name: "7N Architects",
      latitude: center[1],
      longitude: center[0],
      size: [44, 50],
      anchor: [22, 48],
      popup: false,
      image: homeSource
    });
  }, maps);

  if (observations) {
    const observationItems = queryChildren(observations, ".observation-item");
    const observationMore = queryChild(observations, ".observations__more");

    const chunkSize = 6;
    const moreClick = K.fromEvents(observationMore, "click");
    const totalObservations = K.constant(observationItems.length);
    const visibleCount = moreClick.scan(
      previous => previous + chunkSize,
      chunkSize
    );
    const visibleObservations = visibleCount.map(count =>
      observationItems.slice(0, count)
    );
    const expandable = K.combine(
      [visibleCount, totalObservations],
      (count, total) => count < total
    ).toProperty();

    observationItems.forEach(o => {
      o.classList.add("is-hidden");
    });

    visibleObservations.observe(os => {
      os.forEach(o => {
        o.classList.remove("is-hidden");
      });
    });

    expandable.observe(e => {
      toggleClass("is-expandable", observationMore, e);
    });

    // Delay 1 in order to not have the last click trigger opening a new tab
    moreClick.filterBy(expandable.delay(1)).observe(event => {
      event.preventDefault();
    });
  }

  // Grid Preview
  const gridElement = queryOne(".grid-preview");
  const gridCode = keyDown.map(e => e.which).slidingWindow(3, 3);
  const successfulCode = gridCode.filter(all(equals(71))).map(() => true);
  const gridDisplayed = K.merge([successfulCode, escapeDown.map(() => false)]);

  gridDisplayed.observe(displayed => {
    toggleClass("is-displayed", gridElement, displayed);
  });

  // Post Links open in new window
  textLinks.forEach(link => {
    link.setAttribute("target", "_blank");
  });
});



// WEBPACK FOOTER //
// ./app/javascript/packs/application.js